import React from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  FileText,
  Paintbrush,
  Armchair,
  Home,
  Zap,
  Handshake,
  Key,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Clock,
  Sparkles,
  MessageSquare,
} from "lucide-react";
import { motion } from "framer-motion";

export const servicesData = [
  {
    id: "property-management",
    title: "Full-Stack Property Management",
    tagline: "Total peace of mind for residential & NRI owners",
    category: "management",
    badge: "Most Popular",
    icon: Building2,
    color: "from-red-500 to-rose-600",
    lightBg: "bg-red-50 text-red-600 border-red-100",
    turnaround: "Ongoing 24/7 Care",
    pricing: "Custom Tailored Plans",
    description:
      "End-to-end management for apartments and villas. From tenant screening and background verification to automated rent collection, regular physical inspections, and repair coordination.",
    highlights: [
      "Rigorous 3-tier tenant background verification",
      "Guaranteed on-time monthly rent remittance",
      "Quarterly detailed photo & video inspection reports",
      "Dedicated 24/7 personal relationship manager",
    ],
    targetUrl: "/property-management",
    image:
      "https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "rental-agreements",
    title: "Online Rental Agreements",
    tagline: "Legally stamped agreements delivered to your doorstep",
    category: "legal",
    badge: "Within 24 Hours",
    icon: FileText,
    color: "from-blue-500 to-indigo-600",
    lightBg: "bg-blue-50 text-blue-600 border-blue-100",
    turnaround: "24-Hour Express",
    pricing: "Starting from ₹499",
    description:
      "Get legally verified, government e-stamped rental agreements drafted by senior legal counsel without ever visiting a sub-registrar office. Fully compliant with Karnataka Rent Control Act.",
    highlights: [
      "Government-authorized e-Stamping included",
      "Lawyer drafted clauses customized for Bangalore",
      "Aadhaar e-Sign enabled digital signature",
      "Physical doorstep delivery of hard copy",
    ],
    targetUrl: "/rental-agreements",
    image:
      "https://images.unsplash.com/photo-1450133064473-71024230f91b?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "e-khata",
    title: "e-Khata Registration & Transfer",
    tagline: "100% BBMP compliant document regularization",
    category: "legal",
    badge: "BBMP Verified",
    icon: ShieldCheck,
    color: "from-emerald-500 to-teal-600",
    lightBg: "bg-emerald-50 text-emerald-600 border-emerald-100",
    turnaround: "Fast-Track Approval",
    pricing: "Transparent Government & Fee Structure",
    description:
      "Seamless processing of e-Khata extract, Khata certificate, mutation, and tax regularization for apartments and plots across all BBMP zones with verified legal oversight.",
    highlights: [
      "Complete document collection from your home",
      "Direct liaison with BBMP revenue officials",
      "Rectification of old manual Khata discrepancies",
      "Guaranteed authentic digital e-Khata certificate",
    ],
    targetUrl: "/e-khata",
    image:
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "bescom-transfer",
    title: "BESCOM Meter Ownership Transfer",
    tagline: "Swift electricity account transfer without the bureaucratic runaround",
    category: "utility",
    badge: "5-Day Guarantee",
    icon: Zap,
    color: "from-amber-500 to-orange-600",
    lightBg: "bg-amber-50 text-amber-600 border-amber-100",
    turnaround: "3–5 Business Days",
    pricing: "Fixed Hassle-Free Fee",
    description:
      "Change electricity bill ownership, transfer deposits, update consumer numbers, and resolve tariff disputes across all BESCOM divisions without standing in long queues.",
    highlights: [
      "Indemnity bond and NOC preparation",
      "Sub-divisional office representation",
      "Security deposit transfer assistance",
      "SMS & WhatsApp live status updates",
    ],
    targetUrl: "/bescom-transfer",
    image:
      "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "painting-services",
    title: "Professional Home Painting & Waterproofing",
    tagline: "Dust-free prep, Royale finishes & 1-year damp-proof warranty",
    category: "interiors",
    badge: "1-Yr Warranty",
    icon: Paintbrush,
    color: "from-pink-500 to-rose-600",
    lightBg: "bg-pink-50 text-pink-600 border-pink-100",
    turnaround: "Starts within 48 Hours",
    pricing: "Starting from ₹8.5 / sq.ft",
    description:
      "Transform your space with certified Asian Paints & Berger experts. Laser-measured quotations, dustless mechanized sanding, wall waterproofing, and complimentary post-paint deep cleanup.",
    highlights: [
      "Mechanized sanding with vacuum extraction",
      "100% genuine Asian Paints / Berger Royale products",
      "Complete furniture masking & floor protection",
      "1-Year warranty against flaking and bubbling",
    ],
    targetUrl: "/painting-services",
    image:
      "https://images.unsplash.com/photo-1589939705384-5185137a7f0f?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "rental-furnitures",
    title: "Rental Furniture & Smart Appliances",
    tagline: "Turnkey luxury furnishings with zero heavy capital investment",
    category: "interiors",
    badge: "Free Delivery & Setup",
    icon: Armchair,
    color: "from-purple-500 to-violet-600",
    lightBg: "bg-purple-50 text-purple-600 border-purple-100",
    turnaround: "72-Hour Rapid Delivery",
    pricing: "Plans from ₹499/mo",
    description:
      "Furnish your living room, master bedroom, or work-from-home corner with designer furniture and energy-efficient appliances. Enjoy free delivery, assembly, and free annual relocation.",
    highlights: [
      "Premium solid wood & ergonomic designer sets",
      "Latest smart appliances with free maintenance",
      "Zero security deposit options for verified professionals",
      "Free doorstep assembly & annual upgrade options",
    ],
    targetUrl: "/rental-furnitures",
    image:
      "https://images.unsplash.com/photo-1555041469-a586c61ea9bc?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "buy-properties",
    title: "Buy Verified Homes & Luxury Villas",
    tagline: "Direct developer pricing and 100% RERA verified title deeds",
    category: "trade",
    badge: "Zero Brokerage On Select",
    icon: Home,
    color: "from-red-600 to-rose-700",
    lightBg: "bg-red-50 text-red-600 border-red-100",
    turnaround: "Instant Site Visits",
    pricing: "Best Market Rate Guarantees",
    description:
      "Discover handpicked apartments, gated villa communities, and penthouses in Bangalore's premier locations like Whitefield, Sarjapur, and North Bengaluru with total title verification.",
    highlights: [
      "Strict legal title verification on every project",
      "Complimentary VIP chauffeur-driven site tours",
      "Pre-negotiated developer launch discounts",
      "End-to-end home loan assistance with top banks",
    ],
    targetUrl: "/buy",
    image:
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "rent-properties",
    title: "Curated Rental Properties",
    tagline: "Verified homes with genuine landlords and fair deposit terms",
    category: "trade",
    badge: "Verified Landlords",
    icon: Key,
    color: "from-teal-500 to-cyan-600",
    lightBg: "bg-teal-50 text-teal-600 border-teal-100",
    turnaround: "Same-Day Move In Options",
    pricing: "Transparent Brokerage & Fees",
    description:
      "Search through thousands of live, verified rental listings. Avoid duplicate ads, fake photos, and exorbitant deposits. Find homes close to major tech parks and top schools.",
    highlights: [
      "100% physically visited and verified properties",
      "Standardized rental agreements & transparent deposits",
      "Assistance with society move-in permissions",
      "Video walkthroughs available before site visit",
    ],
    targetUrl: "/rent",
    image:
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "lease-properties",
    title: "Commercial & Residential Long-Term Lease",
    tagline: "High-value long lease structures for maximum financial stability",
    category: "trade",
    badge: "Bankable Documentation",
    icon: Handshake,
    color: "from-sky-500 to-blue-600",
    lightBg: "bg-sky-50 text-sky-600 border-sky-100",
    turnaround: "Custom Structuring",
    pricing: "Zero Monthly Rent Headaches",
    description:
      "Lock in long-term lease properties (3–5 years) with zero monthly rent commitments. Ideal for IT firms, boutique retail, and families seeking long-term residential security.",
    highlights: [
      "Structured legal escrow and security models",
      "Institutional-grade lease agreement drafting",
      "Premises compliance and NOC check",
      "Dedicated commercial transaction advisory",
    ],
    targetUrl: "/lease",
    image:
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=800&q=80",
  },
  {
    id: "sell-properties",
    title: "Sell & Post Free Property Advertisement",
    tagline: "Reach 2.5 Lakh+ qualified buyers & tenants with zero listing fee",
    category: "trade",
    badge: "Free Listing",
    icon: Sparkles,
    color: "from-orange-500 to-amber-600",
    lightBg: "bg-orange-50 text-orange-600 border-orange-100",
    turnaround: "Live in 15 Minutes",
    pricing: "Free to Post",
    description:
      "Sell or rent out your property faster than ever. Get verified buyer leads directly on WhatsApp with digital promotion, professional photography support, and dedicated lead qualification.",
    highlights: [
      "Instant listing live across HousyHunt network",
      "Direct verified buyer connection with zero spam",
      "Market price valuation guidance for your area",
      "Social media and WhatsApp broadcast amplification",
    ],
    targetUrl: "/sell",
    image:
      "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=80",
  },
];

const ServicesGrid = ({ searchQuery, selectedCategory }) => {

  const filteredServices = servicesData.filter((service) => {
    const matchesCategory =
      selectedCategory === "all" || service.category === selectedCategory;
    const matchesSearch =
      searchQuery.trim() === "" ||
      service.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      service.tagline.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-12 bg-[#F8FAFC]">
      <div className="max-w-7xl mx-auto">
        {/* Section title */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div>
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-50 text-red-600 border border-red-100 text-xs font-bold uppercase tracking-wider mb-3">
              Specialized Offerings
            </span>
            <h2 className="text-3xl sm:text-5xl font-black text-gray-900 tracking-tight">
              Featured Property Services
            </h2>
            <p className="mt-3 text-gray-600 max-w-2xl text-base sm:text-lg">
              Explore our comprehensive suite of verified real estate services.
              Click any service to view full package details, pricing, and process steps.
            </p>
          </div>
          <div className="text-sm font-semibold text-gray-500">
            Showing <span className="text-red-600 font-bold">{filteredServices.length}</span> of {servicesData.length} services
          </div>
        </div>

        {/* Empty State */}
        {filteredServices.length === 0 && (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-200 p-8 shadow-sm">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-800">No matching services found</h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">
              We couldn't find any services matching "{searchQuery}". Try searching for terms like "e-Khata", "Painting", "Rental", or "Management".
            </p>
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (index % 3) * 0.1 }}
                className="group flex flex-col bg-white rounded-3xl overflow-hidden border border-gray-200/90 shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-1.5"
              >
                {/* Image Header with Badge */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

                  {/* Badge */}
                  <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-md text-gray-900 text-xs font-bold px-3.5 py-1.5 rounded-full shadow-md flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-red-600" />
                    {service.badge}
                  </span>

                  {/* Floating Icon */}
                  <div className="absolute top-4 right-4 w-11 h-11 rounded-2xl bg-white/90 backdrop-blur-md shadow-lg flex items-center justify-center text-red-600 border border-white">
                    <Icon className="w-5 h-5" />
                  </div>

                  {/* Title overlay */}
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-xl sm:text-2xl font-black text-white leading-tight">
                      {service.title}
                    </h3>
                    <p className="text-xs text-red-200 font-medium mt-1">
                      {service.tagline}
                    </p>
                  </div>
                </div>

                {/* Body Content */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    {/* Turnaround & Pricing pill tags */}
                    <div className="flex flex-wrap items-center gap-2 mb-4 text-xs font-semibold">
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-gray-100 text-gray-700">
                        <Clock className="w-3.5 h-3.5 text-gray-500" />
                        {service.turnaround}
                      </span>
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-lg bg-red-50 text-red-700">
                        {service.pricing}
                      </span>
                    </div>

                    <p className="text-gray-600 text-sm leading-relaxed mb-5">
                      {service.description}
                    </p>

                    {/* Highlights bullet list */}
                    <div className="space-y-2 mb-6 pt-4 border-t border-gray-100">
                      {service.highlights.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex items-start gap-2.5 text-xs text-gray-700 font-medium">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="pt-4 border-t border-gray-100 flex items-center gap-3">
                    <Link
                      to={service.targetUrl}
                      className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white font-bold text-sm shadow-md shadow-red-600/20 hover:shadow-lg hover:shadow-red-600/30 transition-all duration-200 group/btn"
                    >
                      <span>Explore Service</span>
                      <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Link>

                    <button
                      onClick={() => {
                        const msg = `Hi HousyHunt, I would like to inquire about *${service.title}*. Please assist me with details and pricing.`;
                        window.open(
                          `https://wa.me/918088687429?text=${encodeURIComponent(msg)}`,
                          "_blank"
                        );
                      }}
                      title="Quick Inquiry on WhatsApp"
                      className="w-12 h-12 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-600 hover:bg-emerald-600 hover:text-white flex items-center justify-center transition-all duration-200 shadow-sm shrink-0"
                    >
                      <MessageSquare className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesGrid;
